import {
  DocumentType,
  Option,
  PitbullCountry,
  StepEndpointSecurities,
  StepSevenSecurities,
  Template,
} from "../../types";
import {
  firestoreCollectionByDocumentType,
  firestoreDocumentByDocumentType,
  getCompanyData,
  getCountries,
} from "../../utils";
import axios from "axios";
import StepThree from "../../steps/securities/stepThree";
import StepTwo from "../../steps/securities/stepTwo";
import StepOne from "../../steps/securities/stepOne";
import PrevStep from "../../steps/securities/prevStep";
import StepFour from "../../steps/securities/stepFour";
import StepFive from "../../steps/securities/stepFive";
import { preconfirmationSteps } from "../../steps/securities/preconfirmationSteps";
import {
  onUpdateCompletedForm,
  onUpdateHolder,
  addSendedStep,
} from "../../reducers/securitiesReducer";
import PreconfirmationHeader from "../../components/SecuritiesPreconfirmationHeader/SecuritiesPreconfirmationHeader";
import { getHoldersInformation } from "../../utils/securities";
import {
  CreateDraftProps,
  sendStep,
  completeDraft,
} from "../../connectors/connectors";
import { logAnalyticsAndHotjarEvent } from "../../main";
import { FormikValues } from "formik";
import { AppDispatch, RootState } from "../../app/store";
import { doc, getFirestore, setDoc } from "firebase/firestore";

const onboardingSecurities = async ({
  values,
  dispatch,
  state,
}: {
  values: FormikValues;
  dispatch: AppDispatch;
  state: RootState;
}) => {
  const { favicon, formTitle, logo } = getCompanyData();
  const [countries] = await axios.all([getCountries()]);

  const {
    accountHolders,
    language,
    currentHolder,
    beneficiariesCount,
    holdersData,
  } = state.securities;

  const countriesTyped = countries as PitbullCountry[];

  const countriesOptions: Option[] = countriesTyped?.map(({ name }) => {
    return { value: name, label: name };
  });

  const citizenshipOptions: Option[] = countriesTyped?.map(
    ({ citizenship }) => {
      return { value: citizenship, label: citizenship };
    }
  );

  const db = getFirestore();

  const saveFileToFirestore = (fileName: string, type: DocumentType) => {
    const docRef = doc(
      db,
      firestoreCollectionByDocumentType[type],
      state.securities.draftUUID
    );
    setDoc(
      docRef,
      {
        [firestoreDocumentByDocumentType[type]]: fileName,
        ...(type.includes("DNI") ? { scannedQR: false } : {}),
      },
      { merge: true }
    );
  };

  const template: Template = {
    favicon,
    formTitle,
    logo,
    onboardingType: "SECURITIES",
    preconfirmation: () => ({
      headerContent: <PreconfirmationHeader />,
      steps: [],
      tabs: Array.from({ length: accountHolders }, (_, i) => ({
        label: `Titular ${i + 1}`,
        steps: preconfirmationSteps({
          values,
          dispatch,
          state,
          countriesOptions,
          citizenshipOptions,
          currentHolder: i + 1,
          holdersData,
        }),
      })),
      defaultSelectedKey: ({ state }) => {
        const { currentHolder } = state.securities;
        return (currentHolder - 1).toString();
      },
      disabledKeys: ({ state }) => {
        const { holdersData } = state.securities;
        // disable holders tabs that haven't been completed (there is no data in state)
        return Array.from({ length: accountHolders }, (_, i) => {
          const holderData = holdersData.find(
            (holder) => holder.currentHolder === i + 1
          );
          if (!holderData) {
            return i.toString();
          }
        }).filter((key) => key !== undefined);
      },
      onChangeTab: ({ key, dispatch, state }) => {
        const currentTab = Number(key) + 1;

        if (currentTab !== state.securities.currentHolder) {
          dispatch(onUpdateHolder(currentTab));
        }
      },
      checkboxes: [],
      onSubmit: async ({ state, dispatch, navigate }) => {
        const { accountHolders, holdersData, initialValidationData } =
          state.securities;
        if (accountHolders > holdersData.length) {
          dispatch(onUpdateHolder(holdersData.length + 1));
          navigate("/securities");
        } else {
          const firstHolder = holdersData.find(
            (holder) => holder.currentHolder === 1
          );
          //steps 1 to 6 are sent by the first holder, step 7 has all the additional holders information
          const sendedSteps = firstHolder?.sendedSteps || [];

          const holdersInformation = getHoldersInformation({
            holdersData,
            language,
          });

          const step: StepSevenSecurities = {
            "@type": "7_SECURITY",
            holders: holdersInformation,
          };

          // Previous sent steps
          const previousSteps = sendedSteps.filter(
            (step: StepEndpointSecurities) => step["@type"] !== "7_SECURITY"
          );

          const body: CreateDraftProps["body"] = {
            companyType: "ALLARIA_SECURITY",
            personType: "PHYSICAL",
            email: initialValidationData.email,
            steps: [...previousSteps, step],
          };

          await sendStep({ stepId: 7, body });
          dispatch(addSendedStep(step));

          await completeDraft({ draftUUID: state.securities.draftUUID });
          dispatch(onUpdateCompletedForm(true));
          dispatch({ type: "reset-store" });
          logAnalyticsAndHotjarEvent("onboarding_completed", {
            personType: "SECURITIES",
          });
          navigate(
            `/apertura-exitosa?language=${state.securities.language ?? "es"}`
          );
        }
      },
      submitButtonText: ({ state }) => {
        const { accountHolders, holdersData } = state.securities;
        const allHoldersCompleted = holdersData.length === accountHolders;
        return allHoldersCompleted ? "Enviar" : "Guardar y Continuar";
      },
    }),
    steps: [
      PrevStep({ language, accountHolders, currentHolder }),
      StepOne({
        language,
        countriesOptions,
        currentHolder,
        holdersData,
        saveFileToFirestore,
      }),
      StepTwo({
        language,
        countriesOptions,
        citizenshipOptions,
        currentHolder,
        saveFileToFirestore,
      }),
      StepThree({ language, countriesOptions, currentHolder }),
      StepFour({ language, currentHolder }),
      ...(currentHolder === 1
        ? [StepFive({ language, countriesOptions, beneficiariesCount })]
        : []),
    ],
  };

  return template;
};

export default onboardingSecurities;
